'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from '@/i18n/routing';
import { zodResolver } from '@hookform/resolvers/zod';
import { pagesRoutes } from '@/shared/constants/routes';
import {
  loginFormSchema,
  type LoginFormValues,
} from '@/features/auth/internal/schemas/login-form.schema';
import { loginUser } from '@/features/auth/shared/helpers/browser-clients/session';
import {
  EmailNotVerifiedError,
  InvalidCredentialsError,
  TooManyRetriesError,
} from '@/features/auth/shared/api/errors/client-auth-errors';
import { buildL18nErrorCode } from '@/features/auth/internal/helpers/build-l18n-error-code';
import { useAppStore } from '@/features/shared/app-store';
import { TIME_CONSTANTS_IN_MS } from '@/shared/constants/time';
import { setClientCookie } from '@/shared/utils/client-cookies';
import { VERIFICATION_EMAIL_COOKIE } from '@/features/auth/shared/helpers/constants/verification-cookies';
import { emailVerificationConfig } from '@/features/auth/internal/helpers/configs/email-verification';

export const useLogin = (redirectUrl?: string, defaultEmail?: string) => {
  const router = useRouter();
  const errorsRoot = 'root';
  const callbackUrl = redirectUrl || pagesRoutes.home.href;
  const [tooManyRetries, setTooManyRetries] = useState(false);
  const cartId = useAppStore((s) => s.cart.cartId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: defaultEmail || '',
      password: '',
    },
  });

  const onSubmit = async (form: LoginFormValues) => {
    try {
      await loginUser(form.email, form.password, cartId || null);
      router.push(callbackUrl);
      router.refresh();
    } catch (e: unknown) {
      if (e instanceof EmailNotVerifiedError) {
        setClientCookie(
          VERIFICATION_EMAIL_COOKIE,
          form.email,
          emailVerificationConfig.tokenTtlInSeconds,
        );
        router.push(pagesRoutes.verifyEmail.href);
        return;
      } else if (e instanceof InvalidCredentialsError) {
        setError(errorsRoot, { message: buildL18nErrorCode(e.code) });
      } else if (e instanceof TooManyRetriesError) {
        setTooManyRetries(true);
        setTimeout(() => setTooManyRetries(false), TIME_CONSTANTS_IN_MS.ONE_MINUTE);
        setError(errorsRoot, { message: buildL18nErrorCode(e.code) });
      } else {
        setError(errorsRoot, { message: 'errors.unexpectedError' });
        console.error(e);
        // TODO: Redirection to global error page fallback
      }
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    tooManyRetries,
    onSubmit,
  };
};
